"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

import { Button } from "@/components/Button";
import { cookieConsentZIndexClass } from "@/components/overlay.constants";
import { Link } from "@/i18n/navigation";
import {
  disableMarketingAnalytics,
  getCookieConsent,
  hasConsentedTagsOnPage,
  OPEN_COOKIE_SETTINGS_EVENT,
  reloadPage,
  setCookieConsent,
} from "@/lib/analytics";
import { cn } from "@/lib/utils";

export const CookieConsent = () => {
  const t = useTranslations("cookies");
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!getCookieConsent()) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const openSettings = () => {
      setVisible(true);
    };

    window.addEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
    return () =>
      window.removeEventListener(OPEN_COOKIE_SETTINGS_EVENT, openSettings);
  }, []);

  const accept = async (value: "all" | "essential") => {
    const tagsToRemove = value === "essential" && hasConsentedTagsOnPage();
    if (value === "essential") {
      await disableMarketingAnalytics();
    }

    setCookieConsent(value);
    setVisible(false);
    // Loaded third-party tags keep running until the page reloads without them.
    if (tagsToRemove) {
      reloadPage();
    }
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label={t("dialogLabel")}
      className={cn(
        "fixed inset-x-4 bottom-4 mx-auto max-w-2xl rounded-2xl border border-zinc-200/50 dark:border-white/[0.05] bg-card p-4 shadow-xl sm:inset-x-auto sm:right-6",
        cookieConsentZIndexClass,
      )}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm text-muted-foreground">
          {t.rich("description", {
            privacyLink: (chunks) => (
              <Link
                href="/privacy"
                className="font-medium text-foreground underline underline-offset-4 hover:text-brand"
              >
                {chunks}
              </Link>
            ),
          })}
        </p>
        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => void accept("essential")}
          >
            {t("essentialOnly")}
          </Button>
          <Button variant="brand" size="sm" onClick={() => void accept("all")}>
            {t("acceptAll")}
          </Button>
        </div>
      </div>
    </div>
  );
};
